import { useEffect } from 'react';

import { AuthQueryParams, AuthReasonOptions } from 'js/src/components/auth/auth-consts';
import { useForceUpdate } from 'js/src/components/common/hooks/useForceUpdate';
import { Toast } from 'js/src/components/common/Toast';
import Navigation from 'js/src/libs/navigation';
import { getQueryParam } from 'js/src/libs/querystring';
import QueryStringManipulator from 'js/src/libs/querystring-manipulator';

const AUTH_REASON_MESSAGES: Record<string, string | undefined> = {
    [AuthReasonOptions.AuthStateForbidden]:
        'Your device only supports logging in with email. Please use that option, or use a different device.',
    [AuthReasonOptions.AuthForbidden]:
        "Google couldn't confirm your account just now. Please try signing in again.",
    [AuthReasonOptions.GoogleEmailUnverified]:
        "Google hasn't verified the email on that account, so we can't use it to sign in. Please verify your email with Google, or log in with your email and password.",
};

/**
 * Surface a toast for a `?reason=` query param handed back by the auth pipeline (e.g. a Google
 * login refused for an unverified email), then strip the param so a refresh doesn't repeat it.
 * Shared by the login and signup pages, both of which are landing targets for these redirects.
 */
export const useAuthReasonToast = () => {
    const { forceUpdate } = useForceUpdate();

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            const reason = getQueryParam(AuthQueryParams.REASON);
            const reasonMessage = reason ? AUTH_REASON_MESSAGES[reason] : undefined;
            if (reasonMessage) {
                Toast.info(reasonMessage, undefined, undefined, 8000);
                const newUrl = QueryStringManipulator.removeQueryStringParameter(
                    window.location.href,
                    AuthQueryParams.REASON,
                );
                Navigation.replaceState(newUrl);
                forceUpdate();
            }
        }, 1000);
        return () => clearTimeout(timeoutId);
    }, [forceUpdate]);
};
